import katex from 'katex'
import 'katex/dist/katex.min.css'

/**
 * Renderiza fórmulas LaTeX en elementos del DOM
 * Busca delimitadores LaTeX y los convierte en fórmulas renderizadas
 * 
 * Delimitadores soportados:
 * - Inline: \( ... \) o $ ... $
 * - Display: \[ ... \] o $$ ... $$
 */
export default class MathRenderer {
    constructor(container = document) {
        this.container = container
        this.render()
    }

    /**
     * Renderiza todas las fórmulas LaTeX encontradas en el contenedor
     */
    render() {
        // Buscar y renderizar fórmulas display (\[ ... \] o $$ ... $$)
        this.renderDisplayMath()
        
        // Buscar y renderizar fórmulas inline (\( ... \) o $ ... $)
        this.renderInlineMath()
    }

    /**
     * Renderiza fórmulas en modo display
     */
    renderDisplayMath() {
        // Buscar \[ ... \]
        this.processPattern(/\\\[([\s\S]*?)\\\]/g, true)
        
        // Buscar $$ ... $$ (evitar conflictos con $ inline)
        this.processPattern(/\$\$([\s\S]*?)\$\$/g, true)
    }

    /**
     * Renderiza fórmulas en modo inline
     */
    renderInlineMath() {
        // Buscar \( ... \)
        this.processPattern(/\\\(([\s\S]*?)\\\)/g, false)
        
        // Buscar $ ... $ (solo si no está precedido o seguido por otro $)
        // Usamos lookbehind y lookahead negativos para evitar $$ display
        this.processPattern(/(?<!\$)\$(?!\$)([^\$\n]+?)\$(?!\$)/g, false)
    }

    /**
     * Procesa un patrón de LaTeX y renderiza las fórmulas encontradas
     */
    processPattern(pattern, displayMode) {
        const container = this.container === document ? document.body : this.container
        
        // Obtener todos los nodos de texto
        const walker = document.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (node) => {
                    return this.shouldSkipNode(node) 
                        ? NodeFilter.FILTER_REJECT 
                        : NodeFilter.FILTER_ACCEPT
                }
            },
            false
        )

        const textNodes = []
        let node
        while (node = walker.nextNode()) {
            if (pattern.test(node.textContent)) {
                textNodes.push(node)
            }
        }

        textNodes.forEach(textNode => {
            const parent = textNode.parentNode
            if (!parent || parent.classList.contains('katex')) {
                return
            }

            const text = textNode.textContent
            const matches = [...text.matchAll(new RegExp(pattern.source, pattern.flags))]
            
            if (matches.length === 0) {
                return
            }

            const fragment = document.createDocumentFragment()
            let lastIndex = 0

            matches.forEach(match => {
                // Agregar texto antes del match
                if (match.index > lastIndex) {
                    fragment.appendChild(
                        document.createTextNode(text.substring(lastIndex, match.index))
                    )
                }

                // Renderizar la fórmula
                try {
                    const formula = match[1] ? match[1].trim() : match[0]
                    if (!formula) return
                    
                    const span = document.createElement('span')
                    katex.render(formula, span, {
                        throwOnError: false,
                        displayMode: displayMode,
                        strict: false
                    })
                    fragment.appendChild(span)
                } catch (e) {
                    console.warn('Error renderizando fórmula LaTeX:', e, match[0])
                    // Si falla, mantener el texto original
                    fragment.appendChild(document.createTextNode(match[0]))
                }

                lastIndex = match.index + match[0].length
            })

            // Agregar texto restante
            if (lastIndex < text.length) {
                fragment.appendChild(
                    document.createTextNode(text.substring(lastIndex))
                )
            }

            parent.replaceChild(fragment, textNode)
        })
    }

    /**
     * Verifica si un nodo debe ser ignorado
     */
    shouldSkipNode(node) {
        let parent = node.parentNode
        while (parent && parent !== this.container) {
            if (
                parent.tagName === 'SCRIPT' ||
                parent.tagName === 'STYLE' ||
                parent.classList.contains('katex') ||
                parent.classList.contains('no-math-render')
            ) {
                return true
            }
            parent = parent.parentNode
        }
        return false
    }

    /**
     * Renderiza una fórmula LaTeX y retorna el HTML
     */
    static renderToString(formula, displayMode = false) {
        try {
            return katex.renderToString(formula, {
                throwOnError: false,
                displayMode: displayMode,
                strict: false
            })
        } catch (e) {
            console.warn('Error renderizando fórmula LaTeX:', e, formula)
            return formula
        }
    }
}
